/**
 * Main hook for VulForm React integration
 */

import { useState, useEffect, useCallback } from 'react';
import {
  VulFormApiClient,
  VulFormError,
  FormTemplate,
  ValidationErrors,
  SubmissionResponse,
  FormValidator,
} from '@vulform/core';

interface UseVulFormProps {
  templateId: string;
  apiKey?: string;
  apiUrl?: string;
  prefill?: Record<string, any>;
  onSuccess?: (response: SubmissionResponse) => void;
  onError?: (error: VulFormError) => void;
  onValidationError?: (errors: ValidationErrors) => void;
  enableAnalytics?: boolean;
  spamProtection?: boolean;
}

interface UseVulFormReturn {
  // State
  template: FormTemplate | null;
  loading: boolean;
  error: VulFormError | null;
  submitting: boolean;
  submitted: boolean;
  formData: Record<string, any>;
  validationErrors: ValidationErrors;

  // Actions
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  updateField: (fieldName: string, value: any) => void;
  validateForm: () => Promise<boolean>;
  submitForm: (data?: Record<string, any>) => Promise<void>;
  resetForm: () => void;
  clearErrors: () => void;
}

export function useVulForm(props: UseVulFormProps): UseVulFormReturn {
  const {
    templateId,
    apiKey,
    apiUrl,
    prefill = {},
    onSuccess,
    onError,
    onValidationError,
    enableAnalytics = true,
    spamProtection = true,
  } = props;

  // State
  const [template, setTemplate] = useState<FormTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<VulFormError | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>(prefill);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // API client
  const [apiClient, setApiClient] = useState<VulFormApiClient | null>(null);

  // Initialize API client
  useEffect(() => {
    // Enhanced API key resolution with better environment variable support
    const resolvedApiKey =
      apiKey ||
      process.env.NEXT_PUBLIC_VULFORM_API_KEY ||
      process.env.REACT_APP_VULFORM_API_KEY ||
      process.env.VULFORM_API_KEY;

    if (!resolvedApiKey) {
      setError(
        new VulFormError(
          'MISSING_API_KEY',
          'API key is required. Set NEXT_PUBLIC_VULFORM_API_KEY, REACT_APP_VULFORM_API_KEY, or pass apiKey prop.'
        )
      );
      setLoading(false);
      return;
    }

    try {
      const client = new VulFormApiClient({
        apiKey: resolvedApiKey,
        baseUrl: apiUrl, // Let the API client handle URL resolution
        debug: process.env.NODE_ENV === 'development',
      });
      setApiClient(client);
    } catch (err) {
      setError(
        err instanceof VulFormError
          ? err
          : new VulFormError('CLIENT_INIT_ERROR', 'Failed to initialize API client')
      );
      setLoading(false);
    }
  }, [apiKey, apiUrl]);

  // Load template
  useEffect(() => {
    if (!apiClient || !templateId) return;

    const loadTemplate = async () => {
      try {
        setLoading(true);
        setError(null);

        const templateData = await apiClient.getTemplate(templateId);
        setTemplate(templateData);

        // Set default values from template
        const defaults: Record<string, any> = {};
        templateData.fields.forEach(field => {
          if (field.defaultValue !== undefined) {
            defaults[field.name] = field.defaultValue;
          }
        });

        setFormData(prev => ({ ...defaults, ...prev }));

        // Track form view
        if (enableAnalytics) {
          apiClient.trackEvent({
            type: 'form_view',
            templateId,
            timestamp: Date.now(),
          });
        }
      } catch (err) {
        const error =
          err instanceof VulFormError
            ? err
            : new VulFormError('LOAD_FAILED', 'Failed to load form template');
        setError(error);
        onError?.(error);
      } finally {
        setLoading(false);
      }
    };

    loadTemplate();
  }, [apiClient, templateId, enableAnalytics, onError]);

  // Update individual field
  const updateField = useCallback(
    (fieldName: string, value: any) => {
      setFormData(prev => ({ ...prev, [fieldName]: value }));

      // Clear field error when user starts typing
      if (validationErrors[fieldName]) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }
    },
    [validationErrors]
  );

  // Validate form
  const validateForm = useCallback(async (): Promise<boolean> => {
    if (!template) return false;

    const errors = FormValidator.validateForm(formData, template);
    setValidationErrors(errors);

    const isValid = Object.keys(errors).length === 0;

    if (!isValid) {
      onValidationError?.(errors);

      // Track validation errors
      if (enableAnalytics && apiClient) {
        apiClient.trackEvent({
          type: 'validation_error',
          templateId,
          timestamp: Date.now(),
          metadata: { errorFields: Object.keys(errors) },
        });
      }
    }

    return isValid;
  }, [template, formData, onValidationError, enableAnalytics, apiClient, templateId]);

  // Submit form
  const submitForm = useCallback(
    async (data?: Record<string, any>) => {
      if (!apiClient || !template) return;

      const submitData = data || formData;
      setSubmitting(true);
      setError(null);

      try {
        // Validate first
        const isValid = await validateForm();
        if (!isValid) {
          return;
        }

        // Track submission start
        if (enableAnalytics) {
          apiClient.trackEvent({
            type: 'form_submit',
            templateId,
            timestamp: Date.now(),
            metadata: { fieldCount: Object.keys(submitData).length },
          });
        }

        // Submit
        const response = await apiClient.submitForm({
          templateId,
          data: submitData,
        });

        setSubmitted(true);
        onSuccess?.(response);
      } catch (err) {
        const error =
          err instanceof VulFormError
            ? err
            : new VulFormError('SUBMIT_FAILED', 'Form submission failed');
        setError(error);
        onError?.(error);
      } finally {
        setSubmitting(false);
      }
    },
    [apiClient, template, formData, validateForm, onSuccess, onError, enableAnalytics, templateId]
  );

  // Reset form
  const resetForm = useCallback(() => {
    setFormData(prefill);
    setValidationErrors({});
    setSubmitted(false);
    setError(null);
  }, [prefill]);

  // Clear errors
  const clearErrors = useCallback(() => {
    setValidationErrors({});
    setError(null);
  }, []);

  return {
    // State
    template,
    loading,
    error,
    submitting,
    submitted,
    formData,
    validationErrors,

    // Actions
    setFormData,
    updateField,
    validateForm,
    submitForm,
    resetForm,
    clearErrors,
  };
}

export default useVulForm;
